import React from 'react';
import { useNavigate } from 'react-router-dom';

const FullTestResultSummary = ({ result, attemptId }) => {
  const navigate = useNavigate();

  if (!result) return null;

  const {
    percentage_score,
    total_score,
    max_score,
    correct_answers,
    incorrect_answers,
    skipped_answers,
    total_questions,
    section_scores
  } = result;

  // Tính số khiên tổng (chỉ ước tính tương đối cho toàn bài Flyers: Tối đa 15 khiên = 3 phần x 5 khiên)
  // Thực tế bài Flyers chuẩn có 3 phần: Listening, Reading/Writing, Speaking. 
  // Dựa trên data API cung cấp, tôi sẽ tính tổng số khiên đạt được dựa trên trung bình cộng hoặc tổng trực tiếp.
  // Tuy nhiên, để chính xác theo data API (mỗi section trả về số khiên riêng), ta sẽ hiển thị chi tiết theo từng section.

  const getScoreColor = (percent) => {
    if (percent >= 80) return 'text-green-500';
    if (percent >= 60) return 'text-amber-500';
    return 'text-red-500';
  };

  const getScoreBgColor = (percent) => {
    if (percent >= 80) return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
    if (percent >= 60) return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800';
    return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
  };

  const scoreColorClass = getScoreColor(percentage_score);
  const scoreBgColorClass = getScoreBgColor(percentage_score);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-4 md:p-8 font-body">
      <div className="max-w-4xl mx-auto space-y-8 mt-10">
        
        {/* Header Section */}
        <div className="text-center space-y-4 animate-fade-in-up">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-primary-blue/10 rounded-full mb-4">
            <span className="material-symbols-outlined text-6xl text-primary-blue">
              workspace_premium
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-black text-slate-800 dark:text-white">
            Mock Test Completed!
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-400">
            Great job! Here is how you performed.
          </p>
        </div>

        {/* Overall Score Card */}
        <div className={`rounded-3xl p-8 border-4 shadow-soft animate-fade-in-up delay-100 flex flex-col md:flex-row items-center justify-between gap-8 ${scoreBgColorClass}`}>
            <div className="text-center md:text-left space-y-2">
                <h2 className="text-2xl font-display font-bold text-slate-700 dark:text-slate-300">Overall Score</h2>
                <div className="flex items-baseline gap-2 justify-center md:justify-start">
                    <span className={`text-6xl font-black ${scoreColorClass}`}>{total_score}</span>
                    <span className="text-2xl font-bold text-slate-500">/ {max_score}</span>
                </div>
            </div>
            
            <div className="flex items-center gap-6 bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
                <div className="text-center">
                    <div className="text-3xl font-black text-primary-blue">{percentage_score.toFixed(0)}%</div>
                    <div className="text-sm font-bold text-slate-500 uppercase mt-1">Accuracy</div>
                </div>
                <div className="w-px h-12 bg-slate-200 dark:bg-slate-700"></div>
                <div className="text-center">
                    <div className="text-3xl font-black text-slate-700 dark:text-slate-300">{result.attempted_questions || (total_questions - skipped_answers)}</div>
                    <div className="text-sm font-bold text-slate-500 uppercase mt-1">Attempted</div>
                </div>
                <div className="w-px h-12 bg-slate-200 dark:bg-slate-700"></div>
                <div className="text-center">
                    <div className="text-3xl font-black text-slate-400">{skipped_answers}</div>
                    <div className="text-sm font-bold text-slate-500 uppercase mt-1">Skipped</div>
                </div>
            </div>        </div>

        {/* Section Breakdown */}
        <div className="space-y-6 animate-fade-in-up delay-200">
          <h3 className="text-2xl font-display font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <span className="material-symbols-outlined text-primary-blue">analytics</span>
            Section Breakdown
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {section_scores.map((section, index) => {
              const sectionPercent = (section.score / section.max_score) * 100;
              const isPerfect = section.number_shields === 5;

              return (
                <div key={`${section.section_id}-${index}`} className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-sm border-2 border-slate-100 dark:border-slate-700 hover:border-blue-200 transition-all">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                        <h4 className="text-xl font-display font-bold text-slate-800 dark:text-white mb-1">
                            {section.section_title}
                        </h4>
                        <div className="text-slate-500 font-bold">
                            Score: <span className="text-slate-800 dark:text-white">{section.score}</span> / {section.max_score}
                        </div>
                    </div>
                    {/* Shield Display */}
                    <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((shieldNum) => (
                            <span 
                                key={shieldNum}
                                className={`material-symbols-outlined text-2xl ${shieldNum <= section.number_shields ? 'text-amber-400' : 'text-slate-200 dark:text-slate-600'}`}
                                style={{ fontVariationSettings: "'FILL' 1" }}
                            >
                                shield
                            </span>
                        ))}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="h-4 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-1000 ${
                        sectionPercent >= 80 ? 'bg-green-500' : 
                        sectionPercent >= 60 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${sectionPercent}%` }}
                    />
                  </div>
                  {isPerfect && (
                      <p className="mt-4 text-sm font-bold text-green-500 text-center flex items-center justify-center gap-1">
                          <span className="material-symbols-outlined text-[16px]">celebration</span>
                          Perfect score in this section!
                      </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-8 animate-fade-in-up delay-300 border-t border-slate-200 dark:border-slate-800">
          <button 
            onClick={() => navigate(`/full-mock-test/${attemptId}/review`)}
            className="flex-1 btn btn-primary py-4 text-lg font-bold flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined">rate_review</span>
            Review Mistakes
          </button>
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex-1 btn bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-2 border-slate-200 dark:border-slate-700 hover:border-slate-300 hover:bg-slate-50 py-4 text-lg font-bold flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined">home</span>
            Back to Dashboard
          </button>
        </div>

      </div>
    </div>
  );
};

export default FullTestResultSummary;